import type { VersionedWording } from '@/lib/versioned-labels';

/**
 * How the versioned layer names a protocol, everywhere it has to (refonte
 * modulaire §7).
 *
 * The third of these files, and deliberately a copy rather than a template over
 * a noun: French articles and agreements are not a concatenation, and the
 * referents differ from one object to the next. A criterion is cited by
 * PROTOCOLES; a persona is chosen by an EXPÉRIENCE; a protocol is PLAYED by an
 * expérience (§7.1, « les expériences dont une version taguée le joue »), so
 * every sentence of the retirement names experiences and agrees in the feminine
 * around a masculine subject - « Il n'est plus proposé aux expériences. Celles
 * qui le jouent... ». `developmentHint` names them too, on the persona's
 * pattern (issue #155): the protocol's maquette draws no sentence under that
 * list, and a persona has the same referents.
 *
 * Like its two neighbours, this file is a `.tsx` and holds no component: the two
 * hints of the retirement are JSX for the reason `criterion-wording.tsx` argues
 * at length - a name spliced into a template is ONE text node where the prose
 * around it is its own, Chrome measures each node separately, and the two shapes
 * land the tail of the line a third of a pixel apart.
 *
 * `saveHintNew` is the field #49 added to `VersionedWording`, and this is the
 * only object that carries one: H17 (§16) makes the condition of a NEW protocol
 * longer than the layer's default sentence - the name, the description AND the
 * turn cap of every step - and a grey button whose sentence names two of the
 * three would send somebody hunting for a field that is not missing.
 *
 * The two prefixes are what every `data-testid` of the screen is built from,
 * which is why they are spelled in the singular then the plural: `protocole-carte`
 * and `protocoles-grille` are what `protocoles.spec.ts` and
 * `screenshots-protocoles.mjs` read, and a mark nobody can type is a mark nobody
 * can assert on.
 */
export const PROTOCOL_WORDING: VersionedWording = {
  kind: 'protocol',
  entity: 'protocole',
  list: 'protocoles',
  noun: ['protocole', 'protocoles'],
  newTitle: 'Nouveau protocole',
  createLabel: 'Créer un nouveau protocole',
  saveHintNew: 'Actif dès que le nom, la description et le plafond de chaque étape sont remplis.',
  developmentHint: () =>
    'Un protocole n’est joué que par des expériences : ce sont les seuls référents qui peuvent apparaître ici.',
  subject: (name) => `Protocole « ${name} »`,
  retirement: {
    panelTitle: 'Retirer ce protocole',
    retiredTitle: 'Protocole retiré',
    confirmLabel: 'Retirer le protocole',
    panelHint: (name) => (
      <>
        Il n’est plus proposé aux expériences. Celles qui le jouent gardent un avertissement «
        remplacer le protocole {name} » jusqu’au remplacement. Restauration possible depuis
        l’historique.
      </>
    ),
    retiredHint: (name, day) => (
      <>
        Retiré le {day}. Il n’est plus proposé aux expériences, et les expériences qui le jouent
        portent l’avertissement « remplacer le protocole {name} » jusqu’au remplacement. Les
        sessions passées gardent leur gel.
      </>
    ),
    warningTitle: (name) => `Chacune gardera « remplacer le protocole ${name} »`,
  },
  refusal: {
    nameTaken: 'Ce nom est déjà porté par un autre protocole. Choisissez-en un autre.',
    nameTakenBy: (name) =>
      `« ${name} » porte déjà ce nom. Choisissez-en un autre, ou renommez d’abord l’autre protocole.`,
    retired: 'Ce protocole est retiré. Restaurez-le avant de le modifier.',
  },
};
